import type { TFunction } from "i18next";
import {
    TherapyTool,
    ToolBillingPeriod,
    isAnnualPrice,
    isPaidTool,
    toolPriceCents,
} from "../domain/types";
import { moduleName, toolName } from "./toolCopy";

/** An amount in minor units, in the reader's language. */
export const formatMoney = (cents: number, language: string): string =>
    new Intl.NumberFormat(language, { style: "currency", currency: "EUR" })
        .format(cents / 100);

/**
 * What the catalogue chip says a tool costs.
 *
 * A module with a price of its own says the amount, not "Subscription": the
 * chip is the first and often the only thing read before adopting, and a word
 * that could mean anything between nothing and twenty euros is worse there
 * than no chip at all. Everything else says "Plus", because that is what
 * holds it — it is included in the plan rather than free.
 *
 * `period` is the one the reader's own subscription is billed in, because
 * that is what adopting would cost them: a module goes onto the subscription
 * that is already running rather than starting one of its own. Somebody on a
 * yearly plan shown "5,00 €/mes" would be quoted a tenth of what they are
 * charged.
 */
export const toolPriceLabel = (
    t: TFunction,
    tool: TherapyTool,
    language: string,
    period: ToolBillingPeriod = "monthly",
): string => {
    // No price of its own, and not included in Plus either: it is paid for
    // as part of another module, so the chip names that module — just the
    // name, since the card's action already spells out the whole phrase and
    // a chip that long ran off the edge of the card.
    if (tool.included_with) return moduleName(t, { slug: tool.included_with });
    if (isPaidTool(tool)) {
        const price = formatMoney(toolPriceCents(tool, period), language);
        const label = isAnnualPrice(tool, period)
            ? t("marketplace.priceAnnual", { price })
            : t("marketplace.priceMonthly", { price });
        // Before VAT, like every published price: Stripe adds it at payment.
        return `${label} ${t("subscription.billing.tax.suffix")}`;
    }
    return t("marketplace.pricePlan");
};

/**
 * What a tool that comes with another says instead of a price or a button.
 *
 * Both names when they differ — "Incluida en Terapia sistémica:
 * Constelación" — because the tool is opened as Constellation and paid for as
 * systemic therapy, and either name alone left people unsure what to buy.
 */
export const includedWithLabel = (t: TFunction, slug: string): string => {
    const tool = toolName(t, { slug });
    const module = moduleName(t, { slug });
    return tool === module
        ? t("marketplace.includedWith", { tool })
        : t("marketplace.includedWithModule", { tool, module });
};

/** A day, spelled out in the reader's language. */
export const formatDay = (date: Date, language: string): string =>
    new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(date);
